import React, { useEffect, useState } from "react";
import {
  SACard,
  SAButton,
  SAIcon,
  SAIcons,
  SAIconSize,
  SAModal,
  SAText,
  SAUXTheme
} from '@saux/design-system-react';
import styled from 'styled-components';

const mobileWidth = 600;

type AddContactTypeAlertsProps = {
  title: string;
  text: string;
  note?: boolean;
  onClickClose: Function;
};

type StyleProps = {
  error?: boolean;
  theme?: SAUXTheme;
};

const Modal = styled(SAModal)`
  display: flex;
  top: 50%;
  left: 50%;
  justify-content: center;
  align-items: center;
  overflow: auto;
`;

const AdContactTypeAlerts = styled(SACard)`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: column;
      width: 600px;
      overflow: auto;
      margin: auto;

      section {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        flex-grow: 1;
        width: auto;
        width: 100%;
        height: 100%;
        border-radius: 0;

        section {
          padding: ${theme?.size.spacer.large} ${theme?.size.spacer.medium};
        }
      }
    `;
  }};
`;

const ModalHeader = styled.div`
  padding-bottom: 45px;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 30px;

  button:last-child {
    display: none;

    &:focus {
      outline: auto;
    } 
  }

  @media only screen and (max-width: ${mobileWidth}px) {
    button:last-child {
      display: flex;
    }
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalFooter: any = styled.div`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      padding-top: 60px;

      button {
        font-weight: ${theme?.font.primary.weight.normal};
        margin-left: ${theme?.size.spacer.medium};

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          font-weight: ${theme?.font.primary.weight.bold};
        }
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        justify-content: center;
        height: 100%;
      }
    `;
  }};
`;

const Note = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 30px;

  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    &:first-child {
      font-weight: 700;
    }
  }
`;

const Text = styled(SAText)`
  font-size: 20px;
  line-height: 26px;
`;

export default function ModalAddContactTypeAlerts({ title, text, note, onClickClose }: AddContactTypeAlertsProps) {
  useEffect(() => {
    function focusDefault() {
      const modal = document.querySelector('#modalcaddcontacttypealerts') as HTMLElement;
      if (modal) {
        const element = modal.querySelector('button.sa-button') as HTMLElement;
        if (element) {
          element.focus();
        }
      } 
    }

    function focusModalAddContactTypeAlerts(e: any) {
      const modal = document.querySelector('#modalcaddcontacttypealerts') as HTMLElement;
      const root = document.querySelector('#root') as HTMLElement;
      if (modal && root) {
        if ((e.type === 'focusout' && root.contains(e.relatedTarget)) || (e.type === 'focusin' && root.contains(e.target))) {
          let element = null;
          if (document.documentElement.clientWidth <= mobileWidth) {
            element = modal.querySelector('button') as HTMLElement;
          } else {
            element = modal.querySelector('a') as HTMLElement;
          }
          if (element) {  
            element.focus();
          }
        }
      }
    }

    focusDefault();
    document.addEventListener('focusin', focusModalAddContactTypeAlerts);
    document.addEventListener('focusout', focusModalAddContactTypeAlerts);

    return () => {
      document.removeEventListener('focusin', focusModalAddContactTypeAlerts);
      document.removeEventListener('focusout', focusModalAddContactTypeAlerts);
    }
  }, []);
  
  function handleClickAddContactTypesAlertModal(event: React.MouseEvent) {
    event.stopPropagation();

    // get the card element inside the modal
    const cardElement = document.querySelector('#modal > div > article > section');

    // close modal when clicking outside of the card element
    if (!(cardElement instanceof Element && cardElement.contains(event.target as Node))) {
      onClickClose(event);
    }
  }

  function handleClickClose(event: React.MouseEvent) {
    event.stopPropagation();
    onClickClose(event);
  }

  return (
    <Modal id="modalcontactagencyoperations" onClickHandler={handleClickAddContactTypesAlertModal}>
      <AdContactTypeAlerts variant="minimal">
        <ModalHeader>
          <ModalTitle>
            <SAText type="heading-2" text={title} />
            <SAIcon className="closemodal" icon={SAIcons.x} size={SAIconSize.medium} colorVariant="dark" onClick={handleClickClose} />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Text type="standard" text={text} />
          {note &&
            <>
              <Note>
                <SAText type="standard" text="Note:" />
                <span>To add a Principal, contact Sales Operations at <a href="mailto:SalesOperations@stateauto.com">SalesOperations@stateauto.com</a></span>
              </Note>
            </>
          }
        </ModalBody>
        <ModalFooter>
          <SAButton label="Close" onClick={handleClickClose} variant="large" color="secondary" textTransform="uppercase" />
        </ModalFooter>
      </AdContactTypeAlerts>
    </Modal>
  );
}
